// Joana — a first-party SAMPLE mini-app, used to demonstrate (and test) the
// embedded mini-app host inside the chat shell. It is ordinary untrusted mini-app
// code: it runs in the WASM isolate off the DOM thread, builds its view with the
// catalog (never raw DOM), and reaches the outside world ONLY through declared
// capabilities mediated by the broker —
//   • storage.kv  — runs IN the worker (persist the counter),
//   • share.sheet — UI-bearing, proxied to the trusted main thread.
// It exports a synchronous render() (a pure function of state, the pull model) and
// async handlers for taps. Its actions use the `emit_event` intent (the mini-app
// event path), distinct from the trusted shell's `shell` intent.

import type { MiniAppManifest } from "../runtime/manifest.ts";
import type { MiniAppPackage } from "./types.ts";

const MANIFEST: MiniAppManifest = {
  app_id: "tz.wavetalk.sample.playground",
  limits: { memory_bytes: 16 * 1024 * 1024, cpu_ms_per_call: 1000 },
  capabilities: [
    { name: "storage.kv", reason: "remember your count between visits" },
    { name: "share.sheet", reason: "share your count" },
    // Only these two are granted: storage.kv (in-worker) + share.sheet
    // (proxied to the trusted main thread). Everything else is unreachable.
  ],
};

// The bundle is a plain string evaluated inside the isolate. Kept tiny + literal.
const CODE = `
'use strict';
let count = 0;
let note = 'Welcome 👋';

globalThis.onLaunch = async () => {
  const saved = await sema.storage.get('count');
  count = saved == null ? 0 : (parseInt(saved, 10) || 0);
  note = saved == null ? 'Fresh start' : ('Loaded ' + count);
};

globalThis.inc = () => { count += 1; note = 'Count is ' + count; };
globalThis.reset = () => { count = 0; note = 'Reset'; };

globalThis.save = async () => {
  await sema.storage.set('count', String(count));
  note = 'Saved ' + count;
};

globalThis.doShare = async () => {
  try {
    await sema.share({ text: 'My WaveTalk count is ' + count });
    note = 'Shared!';
  } catch (e) {
    note = 'Share blocked: ' + e.message;
  }
};

globalThis.render = () => ({
  type: 'stack', id: 'mini-root',
  style: { direction: 'column', gap: 14, padding: 20, align: 'stretch' },
  children: [
    { type: 'text', id: 'mini-count', props: { value: String(count) },
      style: { font_size: 52, font_weight: 800, color: 'brand.gradient.end', text_align: 'center' } },
    { type: 'text', id: 'mini-note', props: { value: note },
      style: { font_size: 15, color: 'text.secondary', text_align: 'center' } },
    { type: 'button', id: 'mini-inc', props: { label: '+1' },
      style: { padding: 14, radius: 12, background: 'brand.gradient.end', color: '#FFFFFF', font_size: 18, font_weight: 700, border: 'none' },
      action: { intent: 'emit_event', payload: { name: 'inc' } } },
    { type: 'stack', id: 'mini-row',
      style: { direction: 'row', gap: 10 },
      children: [
        { type: 'button', id: 'mini-save', props: { label: 'Save' },
          style: { flex: 1, padding: 12, radius: 12, background: '#1A1A1A', color: '#FFFFFF', font_weight: 600, border: 'none' },
          action: { intent: 'emit_event', payload: { name: 'save' } } },
        { type: 'button', id: 'mini-share', props: { label: 'Share' },
          style: { flex: 1, padding: 12, radius: 12, background: '#1A1A1A', color: '#FFFFFF', font_weight: 600, border: 'none' },
          action: { intent: 'emit_event', payload: { name: 'doShare' } } },
        { type: 'button', id: 'mini-reset', props: { label: 'Reset' },
          style: { flex: 1, padding: 12, radius: 12, background: '#E4E7EB', color: '#1A1A1A', font_weight: 600, border: 'none' },
          action: { intent: 'emit_event', payload: { name: 'reset' } } },
      ] },
  ],
});
`;

export const SAMPLE_MINIAPP: MiniAppPackage = { manifest: MANIFEST, code: CODE };
